/* GrowthOS — Opportunities tab (record pages).
   Themed list ported from design growthos/Opportunities.jsx. Wired to the real
   per-record opportunities on `localData.opportunity` (fields: name, account,
   industry, stage, amount, closingDate, owner, id — from useDealColumnDefs).
   Add/Edit/View reuse the existing /opportunity/* routes. The design's remove-X
   is omitted (no per-record unlink API for opportunities). */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateFormatter } from '../../../shared/controls/formatters';

const ICONS = {
  opportunity: <path d="M3 3v18h18M7 14l3-3 4 4 5-6" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  calendar: (<><rect x="3" y="4.5" width="18" height="16.5" rx="2.5" /><path d="M16 2.5v4M8 2.5v4M3 9.5h18" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const stageTone = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('won')) return 'green';
  if (v.includes('lost')) return 'red';
  if (v.includes('negoti')) return 'amber';
  if (v.includes('propos')) return 'purple';
  if (v.includes('qualif') || v.includes('analysis') || v.includes('discover')) return 'blue';
  return 'gray';
};
const isWon = (s) => /won/i.test(String(s || ''));
const isLost = (s) => /lost/i.test(String(s || ''));

const fmtValue = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  if (!isFinite(n) || n === 0) return typeof v === 'string' && v.trim() ? v : '—';
  return '$' + (Math.abs(n) >= 1000 ? (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'K' : String(n));
};
const dateStr = (v) => {
  if (!v) return '';
  try {
    const s = DateFormatter({ value: v });
    return s && s !== 'Invalid date' ? s : '';
  } catch {
    return '';
  }
};

const AVA = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initialsOf = (name) =>
  String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const avaColor = (name) => AVA[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVA.length];

function OppRow({ o, isLast, canEdit, onEdit }) {
  const tone = stageTone(o.stage);
  const close = dateStr(o.closingDate);
  const meta = [o.account, o.industry].filter(Boolean);
  return (
    <div className={'gos-oppr-row' + (isLast ? ' last' : '')}>
      <div className="gos-oppr-ic" style={{ background: `var(--gos-${tone}-bg)`, color: `var(--gos-${tone}-fg)` }}>
        <Ic name="opportunity" size={17} />
      </div>
      <div className="gos-oppr-main">
        <div className="gos-oppr-name">
          {o.id ? (
            <Link to={`/opportunity/viewOpportunity?id=${o.id}`} state={o}>
              {o.name || '(untitled)'}
            </Link>
          ) : (
            o.name || '(untitled)'
          )}
        </div>
        <div className="gos-oppr-meta">
          {o.account ? (
            <>
              <Ic name="building" size={12} />
              <span>{o.account}</span>
            </>
          ) : null}
          {o.industry ? (
            <>
              {o.account ? <span className="gos-oppr-dot">·</span> : null}
              <span>{o.industry}</span>
            </>
          ) : null}
          {close ? (
            <>
              {meta.length ? <span className="gos-oppr-dot">·</span> : null}
              <Ic name="calendar" size={12} />
              <span>{(isWon(o.stage) ? 'Closed ' : 'Close ') + close}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="gos-oppr-col-stage">
        <span className={`gos-oppr-badge ${tone}`}>
          <span className="gos-oppr-pip" />
          {o.stage || '—'}
        </span>
      </div>
      <div className="gos-oppr-value">{fmtValue(o.amount)}</div>
      <div className="gos-oppr-owner">
        {o.owner ? (
          <>
            <span className="gos-oppr-avatar" style={{ background: avaColor(o.owner) }}>{initialsOf(o.owner)}</span>
            <span className="gos-oppr-owner-name">{o.owner}</span>
          </>
        ) : (
          <span className="gos-oppr-owner-name">—</span>
        )}
      </div>
      <div className="gos-oppr-actions">
        {canEdit ? (
          <button type="button" className="gos-oppr-iconbtn" title="Edit opportunity" onClick={() => onEdit(o)}>
            <Ic name="edit" size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

const Opportunities = ({ localData, module, hasUpdateOpportunityPermission = true }) => {
  const navigate = useNavigate();
  const all = useMemo(() => (Array.isArray(localData?.opportunity) ? localData.opportunity : []), [localData]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const counts = useMemo(
    () => ({
      all: all.length,
      open: all.filter((o) => !isWon(o.stage) && !isLost(o.stage)).length,
      won: all.filter((o) => isWon(o.stage)).length,
    }),
    [all]
  );

  const rows = all
    .filter((o) => (filter === 'all' ? true : filter === 'open' ? !isWon(o.stage) && !isLost(o.stage) : isWon(o.stage)))
    .filter((o) => `${o.name || ''} ${o.account || ''} ${o.industry || ''}`.toLowerCase().includes(query.toLowerCase()));

  const TABS = [
    { id: 'all', label: 'All' },
    { id: 'open', label: 'Open' },
    { id: 'won', label: 'Won' },
  ];

  const onAdd = () => navigate('/opportunity/addOpportunity', { state: { from: module } });
  const onEdit = (o) => navigate('/opportunity/editOpportunity', { state: { ...o, type: 'edit' } });

  return (
    <div className="gos-oppr">
      <div className="gos-oppr-card">
        <div className="gos-oppr-head">
          <div className="gos-oppr-title">
            <Ic name="opportunity" size={16} />
            Opportunities
            <span className="gos-oppr-count">{all.length}</span>
          </div>
          {hasUpdateOpportunityPermission ? (
            <button type="button" className="gos-oppr-btn soft" onClick={onAdd}>
              <Ic name="plus" size={13} />
              Add Opportunity
            </button>
          ) : null}
        </div>

        <div className="gos-oppr-filter">
          <div className="gos-oppr-seg">
            {TABS.map((t) => (
              <button type="button" key={t.id} className={filter === t.id ? 'active' : ''} onClick={() => setFilter(t.id)}>
                {t.label}
                <span className="gos-oppr-seg-n">{counts[t.id]}</span>
              </button>
            ))}
          </div>
          <div className="gos-oppr-search">
            <Ic name="search" size={15} />
            <input placeholder="Search opportunities…" value={query} onChange={(e) => setQuery(e.target.value)} />
            {query ? (
              <button type="button" className="gos-oppr-search-clear" onClick={() => setQuery('')}>
                <Ic name="x" size={12} />
              </button>
            ) : null}
          </div>
        </div>

        {rows.length ? (
          <>
            <div className="gos-oppr-head-row">
              <span className="gos-oppr-col-name">Opportunity</span>
              <span className="gos-oppr-col-stage">Stage</span>
              <span className="gos-oppr-col-value">Value</span>
              <span className="gos-oppr-col-owner">Owner</span>
              <span className="gos-oppr-col-actions" />
            </div>
            <div className="gos-oppr-list">
              {rows.map((o, i) => (
                <OppRow key={o.id ?? i} o={o} isLast={i === rows.length - 1} canEdit={hasUpdateOpportunityPermission} onEdit={onEdit} />
              ))}
            </div>
          </>
        ) : (
          <div className="gos-oppr-empty">
            <div className="gos-oppr-empty-ic">
              <Ic name="opportunity" size={21} />
            </div>
            <div className="gos-oppr-empty-t">{query ? 'No opportunities match' : `No ${filter} opportunities`}</div>
            <div className="gos-oppr-empty-s">
              {query ? `Nothing matches “${query}”.` : "Opportunities linked to this record will appear here."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Opportunities;
